<template>
  <modal-frame
    :isVisible="props.isVisible"
    :width="'400px'"
    :isOverlay="false"
    @closeDialogHandler="closeHandler(0)"
  >
    <div class="modal_title">
      <h4>{{ $t("trading.positions.tpslep") }}</h4>
      <button class="close-btn" @click="closeHandler(0)">×</button>
    </div>
    <div class="modal_body">
      <div class="symbol-info">
        <div>
          <p>{{ $t("trading.table.symbol") }}</p>
          <span :class="tpslInfo.side === 'buy' ? 'txt-plus' : 'txt-minus'"
            >{{ tpslInfo.symbol }}USDT {{ $t("trading.positions.perp") }} /
            {{
              tpslInfo.side === "buy"
                ? $t("trading.table.long")
                : $t("trading.table.short")
            }}
            {{ tpslInfo.leverage }}x</span
          >
        </div>
        <div>
          <p>{{ $t("trading.table.entryPrice") }}</p>
          <span>{{ formatNumberWithComma(tpslInfo.entry_price) }} USDT</span>
        </div>
      </div>
      <div class="gray-line"></div>
      <div class="tpsl">
        <div class="info">
          <div class="title-area">
            <p>{{ $t("trading.transact.tp") }}</p>
            <button
              v-if="tpslInfo.tp && Number(tpslInfo.tp) !== 0"
              @click="cancelPositionTpsl('tp')"
            >
              {{ $t("trading.table.cancel") }}
            </button>
          </div>
          <div class="input-area" :class="{ error: errorTp }">
            <input type="text" v-model="tp" @input="tpslInputHandler($event, 'tp')" />
            <input
              type="text"
              v-model="tpPnl"
              @input="pnlInputHandler($event, 'tppnl')"
            />
            <p class="ab">PNL</p>
          </div>
          <span v-if="errorTp" class="error-sent">{{ errorTp }}</span>
        </div>
        <div>
          <div class="title-area">
            <p>{{ $t("trading.transact.sl") }}</p>
            <button
              v-if="tpslInfo.sl && Number(tpslInfo.sl) !== 0"
              @click="cancelPositionTpsl('sl')"
            >
              {{ $t("trading.table.cancel") }}
            </button>
          </div>
          <div class="input-area" :class="{ error: errorSl }">
            <input type="text" v-model="sl" @input="tpslInputHandler($event, 'sl')" />
            <input
              type="text"
              v-model="slPnl"
              @input="pnlInputHandler($event, 'slpnl')"
            />
            <p class="ab">PNL</p>
          </div>
          <span v-if="errorSl" class="error-sent">{{ errorSl }}</span>
        </div>
      </div>
    </div>
    <div class="col-12 text-center modal_btn">
      <button type="button" class="btn btn-primary mlr-5" @click="closeHandler(1)">
        {{ $t("trading.common.confirm") }}
      </button>
    </div>
  </modal-frame>
  <ModalAlert
    :isVisible="alertDialog.isVisible"
    :content="alertDialog.content"
    @closeDialogHandler="alertDialog.isVisible = false"
    :is-btn="true"
  />
</template>

<script setup>
import ModalFrame from "@/components/common/modal/ModalFrame.vue";
import ModalAlert from "@/components/common/modal/ModalAlert.vue";
import { defineProps, defineEmits, ref, computed, watch } from "vue";
import { formatDecimal, formatNumberWithComma, inputNumCheck } from "@/utils/order";
import { useStore } from "vuex";
import { selectedSymbolData } from "@/utils/symbol";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const emit = defineEmits(["closeDialogHandler", "reloadTpslInfo"]);
const props = defineProps({
  isVisible: Boolean,
  tpslInfo: Object,
});
const alertDialog = ref({
  isVisible: false,
  content: "",
});
const tp = ref(null);
const sl = ref(null);
const tpPnl = ref(null);
const slPnl = ref(null);
const symbolData = ref(null);
const errorTp = ref(null);
const errorSl = ref(null);
const lastPrice = computed(() => store.state.transact.lastPrice);
const userNo = computed(() => store.state.account.userNo);
const reCode = computed(() => store.state.user_history.reCode);

const calculatePnl = (price) => {
  if (price === "") return "";
  const entry = props.tpslInfo.entry_price;
  const rate = (price - entry) / entry;
  const leverage = props.tpslInfo.leverage;
  const margin = props.tpslInfo.margin;
  const side = props.tpslInfo.side;

  let pnl = (side === "buy" ? rate : -rate) * leverage * margin;

  // SL인 경우, 손실 보장: 양수면 음수
  // if (!isTp && pnl > 0) pnl = -pnl;

  return Math.floor(pnl * 10000) / 10000;
};

const calculatePrice = (pnl) => {
  if (pnl === "-" || pnl === "") return;
  const entry = props.tpslInfo.entry_price;
  const leverage = props.tpslInfo.leverage;
  const margin = props.tpslInfo.margin;
  const side = props.tpslInfo.side;

  const rate = Number(pnl) / (leverage * margin); // 부호 포함

  const price = side === "buy" ? entry * (1 + rate) : entry * (1 - rate);

  return price <= 0 ? null : formatDecimal(price, symbolData.value?.pricePrecision || 2);
};

const fetchPostionTpsl = async (reqInfo) => {
  await store.dispatch("user_history/postPositionTpsl", reqInfo);
  if (reCode.value !== 0) {
    alertDialog.value.isVisible = true;
    alertDialog.value.content = t(`trading.error.${reCode.value}`);
  } else {
    await store.dispatch("user_history/fetchAllHistory", userNo.value);
    emit("closeDialogHandler");
  }
};
const buildTpslRequest = (overrides = {}) => ({
  user_no: userNo.value,
  position_id: props.tpslInfo.position_id,
  tp: tp.value && !errorTp.value ? Number(tp.value) : 0,
  sl: sl.value && !errorSl.value ? Number(sl.value) : 0,
  ...overrides,
});

const cancelPositionTpsl = async (type) => {
  const overrides = type === "tp" ? { tp: 0 } : { sl: 0 };
  await fetchPostionTpsl(buildTpslRequest(overrides));
  emit("reloadTpslInfo");
};

// const errorFormatted = (type) => {
//   //entry : 현재가로 변경
//   const entry = Number(lastPrice.value);
//   const side = props.tpslInfo.side;
//   const val = type === "tp" || type === "tpPnl" ? Number(tp.value) : Number(sl.value);
//   if (type === "tp" && tp.value === "") {
//     errorTp.value = null;
//     return;
//   } else if (type === "sl" && sl.value === "") {
//     errorSl.value = null;
//     return;
//   }
//   if (side === "buy") {
//     if (type === "tp" && val <= entry) {
//       errorTp.value = t(`trading.positions.sent04`, {
//         num: formatNumberWithComma(entry),
//       });
//     } else if (type === "sl" && val >= entry) {
//       errorSl.value = t(`trading.positions.sent05`, {
//         num: formatNumberWithComma(entry),
//       });
//     } else {
//       if (type === "tp") errorTp.value = null;
//       if (type === "sl") errorSl.value = null;
//     }
//   } else if (side === "sell") {
//     if (type === "tp" && val >= entry) {
//       errorTp.value = t(`trading.positions.sent06`, {
//         num: formatNumberWithComma(entry),
//       });
//     } else if (type === "sl" && val <= entry) {
//       errorSl.value = t(`trading.positions.sent07`, {
//         num: formatNumberWithComma(entry),
//       });
//     } else {
//       if (type === "tp") errorTp.value = null;
//       if (type === "sl") errorSl.value = null;
//     }
//   }
// };

// 청산가에 따른 SL 검증
const validateStopLoss = () => {
  const liqPrice = Number(props.tpslInfo.liq_price);
  const side = props.tpslInfo.side;

  if (side === "buy") {
    if (sl.value <= liqPrice) {
      errorSl.value = t(`trading.positions.sent08`, {
        num: formatNumberWithComma(liqPrice),
      });
    } else {
      errorSl.value = null;
    }
  } else {
    if (sl.value >= liqPrice) {
      errorSl.value = t(`trading.positions.sent09`, {
        num: formatNumberWithComma(liqPrice),
      });
    } else {
      errorSl.value = null;
    }
  }
};

const tpslInputHandler = (e, type) => {
  const val = e.target.value;
  const pricePrecision = symbolData.value.pricePrecision;
  const liqPrice = Number(props.tpslInfo.liq_price);

  let formattedValue = inputNumCheck(val, pricePrecision);
  e.target.value = formattedValue;

  if (type === "tp") {
    tp.value = formattedValue;
    tpPnl.value = calculatePnl(tp.value);
  } else {
    sl.value = formattedValue;
    slPnl.value = calculatePnl(sl.value);
    if (liqPrice !== 0 && sl.value !== "" && sl.value != 0) {
      validateStopLoss();
      if (errorSl.value) {
        return;
      }
    }
  }
  // 오류 메시지 처리
  // errorFormatted(type);
};

const pnlInputHandler = (e, type) => {
  let val = e.target.value.replace(/[^0-9.]/g, "");

  const parts = val.split(".");
  if (parts.length === 2) {
    parts[1] = parts[1].slice(0, 4);
    val = parts[0] + "." + parts[1];
  }

  if (type === "slpnl") {
    if (!val.startsWith("-")) {
      val = "-" + val;
    }
  }

  e.target.value = val;

  if (type === "tppnl") {
    tpPnl.value = val;
    tp.value = calculatePrice(val);
  } else {
    slPnl.value = val;
    sl.value = calculatePrice(val);

    const liqPrice = Number(props.tpslInfo.liq_price);
    if (liqPrice !== 0 && sl.value !== "" && sl.value != 0) {
      validateStopLoss();
      if (errorSl.value) {
        return;
      }
    }
  }

  // errorFormatted(type === "tppnl" ? "tp" : "sl");
};

const closeHandler = (isConfirm) => {
  if (isConfirm) {
    fetchPostionTpsl(buildTpslRequest());
  } else {
    emit("closeDialogHandler");
  }
  // tp.value = null;
  // sl.value = null;
  // tpPnl.value = null;
  // slPnl.value = null;
};

watch(
  () => props.tpslInfo,
  () => {
    if (!props.tpslInfo) return;
    symbolData.value = selectedSymbolData(props.tpslInfo.symbol);
    if (props.tpslInfo.tp && props.tpslInfo.tp !== "0.0") {
      tp.value = formatDecimal(props.tpslInfo.tp, symbolData.value?.pricePrecision || 2);
      tpPnl.value = calculatePnl(tp.value);
    } else {
      tp.value = null;
      tpPnl.value = null;
    }
    if (props.tpslInfo.sl && props.tpslInfo.sl !== "0.0") {
      sl.value = formatDecimal(props.tpslInfo.sl, symbolData.value?.pricePrecision || 2);
      slPnl.value = calculatePnl(sl.value);
    } else {
      sl.value = null;
      slPnl.value = null;
    }
    errorSl.value = null;
    errorTp.value = null;
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.modal_body {
  * {
    font-family: (--main-font-Light), "Noto Sans JP", "Noto Sans SC";
  }
}

.symbol-info {
  display: flex;
  flex-direction: column;
  gap: 10px;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;

    p {
      font-weight: 400;
      color: var(--txt-gray-color);
    }
  }
}

.info {
  margin-bottom: 30px;
}

.title-area {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  p {
    font-size: 1.6rem;
    font-weight: 500;
  }

  button {
    background-color: transparent;
    color: #d3be04;
  }
}

.input-area {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;

  &.error {
    input {
      border: 1px solid #d31c04 !important;
    }
  }

  input {
    background-color: var(--card-color);
    border: 1px solid var(--txt-gray-color);
    border-radius: 5px;
    color: var(--txt-default-color);
    padding: 10px;

    &:first-of-type {
      width: 65%;
    }

    &:last-of-type {
      width: 35%;
      padding-right: 40px;
    }

    &:focus {
      border: 1px solid #d3be04;
    }
  }

  .ab {
    position: absolute;
    right: 10px;
  }
}

.error-sent {
  color: #d31c04;
  font-weight: 400;
  margin-top: 10px;
  display: inline-block;
}
</style>
